import { roundAwayFromZero } from "~/utils/round-away-from-zero";

/** A priced basket line, as either the kiosk or the attended till holds it. */
type TaxableLine = {
  unitTotalPriceInclVat: number;
  unitTotalPriceExclVat: number;
  vatRate: number;
  /** The line's deposit. Equal on both bases for a scheme that does not tax it. */
  depositTotalInclVat: number;
  depositTotalExclVat: number;
};

type ResolveBasketTaxTotalsArgs = {
  showPricesInclVat: boolean;
  items: TaxableLine[];
};

/**
 * The basket's tax rows: the excl subtotal and one tax line per rate,
 * ascending, or null when prices are shown incl VAT or the basket carries no
 * tax. Excl line totals are rounded the way the lines are displayed, so the
 * rows reconcile with what is on screen.
 *
 * The deposit fee stays out of the subtotal — it gets its own summary row —
 * but a `fromProduct` scheme taxes it at the product's rate, and that VAT has
 * to land in the tax line or the rows stop summing to what is charged.
 */
export function resolveBasketTaxTotals({
  showPricesInclVat,
  items
}: ResolveBasketTaxTotalsArgs): {
  totalExclVat: number;
  taxLines: { rate: number; taxAmount: number }[];
} | null {
  if (showPricesInclVat) {
    return null;
  }

  let totalExclVat = 0;
  const taxByRate = new Map<number, number>();
  for (const item of items) {
    const exclVat = roundAwayFromZero(item.unitTotalPriceExclVat);
    totalExclVat += exclVat;
    const depositTax = item.depositTotalInclVat - item.depositTotalExclVat;
    const taxAmount = item.unitTotalPriceInclVat - exclVat + depositTax;
    taxByRate.set(item.vatRate, (taxByRate.get(item.vatRate) ?? 0) + taxAmount);
  }

  const taxLines = [...taxByRate.entries()]
    .filter(([, taxAmount]) => taxAmount !== 0)
    .map(([rate, taxAmount]) => ({ rate, taxAmount }))
    .sort((a, b) => a.rate - b.rate);

  if (taxLines.length === 0) {
    return null;
  }

  return { totalExclVat, taxLines };
}
